import { type FormEvent, useReducer, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";
import {
  StockPoolDialog,
  type StockPoolDialogKind,
} from "~/components/stock-pool-dialogs";
import {
  AdminApiError,
  fetchAdminInventory,
  setCampaignQuotaCapacity,
} from "~/lib/admin-api";
import {
  acceptingNewLabels,
  buildAdminInventoryQuery,
  buildCampaignQuotaRequest,
  canExecuteCampaignQuota,
  campaignQuotaReducer,
  capacitySourceLabels,
  createExecuteRequest,
  findCampaignQuotaRow,
  formatCapacityCount,
  formatInventoryGrams,
  getInitialJstDateRange,
  initialCampaignQuotaState,
  isShippingHoliday,
  normalizeMonthInput,
  resolveCampaignQuotaRow,
  validateDailyDateRange,
} from "~/lib/admin-inventory";
import {
  dateDisplayProps,
  formatDate,
  formatDateTime,
  formatMonth,
  formatVariety,
} from "~/lib/format";
import type {
  AdminCampaignQuotaRow,
  AdminDailyAllocationCapacityRow,
  AdminInventoryResponse,
  AdminStockPoolRow,
} from "~/types/admin-inventory";

const permissionErrorMessage =
  "管理者権限がありません。権限が変更された可能性があります — 再ログインしてください。";
const inventoryCardClass =
  "rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none";
const inventoryControlClass =
  "rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none focus-visible:border-amber-500 focus-visible:ring-amber-500/40";
const inventoryPrimaryButtonClass =
  "rounded-none border border-amber-500 bg-amber-500 text-stone-950 shadow-none hover:bg-amber-400 focus-visible:border-amber-500 focus-visible:ring-amber-500/40";
const inventorySecondaryButtonClass =
  "rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none hover:bg-zinc-100 focus-visible:border-amber-500 focus-visible:ring-amber-500/40";
const inventoryAlertClass =
  "rounded-none border-amber-300 bg-white text-zinc-900 shadow-none";

function shouldRetry(failureCount: number, error: Error) {
  if (
    error instanceof AdminApiError &&
    error.status >= 400 &&
    error.status < 500
  ) {
    return false;
  }
  return failureCount < 2;
}

function loadErrorMessage(error: Error): string {
  if (
    error instanceof AdminApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return permissionErrorMessage;
  }
  return error.message;
}

type QuotaDialogTarget =
  { mode: "create" } | { mode: "update"; quota: AdminCampaignQuotaRow };

type StockDialogTarget = {
  kind: StockPoolDialogKind;
  pool: AdminStockPoolRow;
};

function StockPoolsTable({
  rows,
  onOpen,
}: {
  rows: AdminStockPoolRow[];
  onOpen: (kind: StockPoolDialogKind, pool: AdminStockPoolRow) => void;
}) {
  return (
    <Table aria-labelledby="stock-pools-heading">
      <TableHeader>
        <TableRow>
          <TableHead>品種</TableHead>
          <TableHead>年産</TableHead>
          <TableHead>予約開放日</TableHead>
          <TableHead>出荷開始日</TableHead>
          <TableHead>容量（g）</TableHead>
          <TableHead>予約済み（g）</TableHead>
          <TableHead>残量（g）</TableHead>
          <TableHead>新規受付</TableHead>
          <TableHead>更新日時</TableHead>
          <TableHead>操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={10} className="py-8 text-center text-zinc-500">
              年産在庫はありません。
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{formatVariety(row.variety)}</TableCell>
              <TableCell>{row.crop_year}</TableCell>
              <TableCell {...dateDisplayProps(row.opened_at, formatDate)} />
              <TableCell
                {...dateDisplayProps(row.shipping_starts_on, formatDate)}
              />
              <TableCell>
                {formatInventoryGrams(row.capacity_brown_rice_g)}
              </TableCell>
              <TableCell>
                {formatInventoryGrams(row.reserved_brown_rice_g)}
              </TableCell>
              <TableCell>
                {formatInventoryGrams(row.available_brown_rice_g)}
              </TableCell>
              <TableCell>
                <Badge
                  className="rounded-none border-zinc-300 bg-zinc-100 text-zinc-900 shadow-none"
                  variant={row.accepting_new ? "default" : "secondary"}
                >
                  {
                    acceptingNewLabels[
                      String(row.accepting_new) as "true" | "false"
                    ]
                  }
                </Badge>
              </TableCell>
              <TableCell
                {...dateDisplayProps(row.updated_at, formatDateTime)}
              />
              <TableCell>
                <div className="flex min-w-48 flex-wrap gap-2">
                  <Button
                    className="rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none hover:bg-zinc-100 focus-visible:border-amber-500 focus-visible:ring-amber-500/40"
                    variant="outline"
                    size="sm"
                    onClick={() => onOpen("capacity", row)}
                  >
                    容量を補正
                  </Button>
                  <Button
                    className="rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none hover:bg-zinc-100 focus-visible:border-amber-500 focus-visible:ring-amber-500/40"
                    variant="outline"
                    size="sm"
                    onClick={() => onOpen("accepting", row)}
                  >
                    受付変更
                  </Button>
                  <Button
                    className="rounded-none border-amber-500 bg-amber-500 text-stone-950 shadow-none hover:bg-amber-400 focus-visible:border-amber-500 focus-visible:ring-amber-500/40"
                    size="sm"
                    onClick={() => onOpen("shortage", row)}
                  >
                    物理不足を記録
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function DailyCapacitiesTable({
  rows,
}: {
  rows: AdminDailyAllocationCapacityRow[];
}) {
  return (
    <Table aria-labelledby="daily-capacities-heading">
      <TableHeader>
        <TableRow>
          <TableHead>日付</TableHead>
          <TableHead>容量（件）</TableHead>
          <TableHead>消費（件）</TableHead>
          <TableHead>残り（件）</TableHead>
          <TableHead>値の由来</TableHead>
          <TableHead>更新日時</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="py-4">
              <Alert variant="warning" className={inventoryAlertClass}>
                <AlertDescription>
                  日次出荷枠を取得できませんでした。
                </AlertDescription>
              </Alert>
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => {
            const holiday = isShippingHoliday(row.allocation_date);
            // 休業日は割当候補から除外されるため枠の数値に意味がない。
            // ただし休業日に保存済み行がある場合は想定外（要調査）なので、
            // 数値と由来を隠さず両方のバッジで目立たせる。
            const anomaly = holiday && row.source === "stored";
            const showCounts = !holiday || anomaly;
            return (
              <TableRow key={row.allocation_date}>
                <TableCell
                  {...dateDisplayProps(row.allocation_date, formatDate)}
                />
                <TableCell>
                  {showCounts ? formatCapacityCount(row.capacity) : "—"}
                </TableCell>
                <TableCell>
                  {showCounts ? formatCapacityCount(row.consumed) : "—"}
                </TableCell>
                <TableCell>
                  {showCounts ? formatCapacityCount(row.available) : "—"}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-2">
                    {holiday ? (
                      <Badge
                        variant="outline"
                        className="rounded-none border-amber-500 bg-transparent text-amber-700 shadow-none"
                      >
                        出荷休業日
                      </Badge>
                    ) : null}
                    {!holiday || anomaly ? (
                      <Badge
                        className={
                          anomaly
                            ? "rounded-none border-amber-300 bg-amber-100 text-amber-800 shadow-none"
                            : "rounded-none border-zinc-300 bg-zinc-100 text-zinc-900 shadow-none"
                        }
                        variant={
                          row.source === "stored" ? "default" : "secondary"
                        }
                        title={
                          anomaly
                            ? "休業日に保存済みの枠行があります（割当は乗らない想定のため要調査）"
                            : undefined
                        }
                      >
                        {capacitySourceLabels[row.source]}
                      </Badge>
                    ) : null}
                  </span>
                </TableCell>
                <TableCell
                  {...dateDisplayProps(row.updated_at, formatDateTime)}
                />
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}

function inventoryRows(data: AdminInventoryResponse | undefined) {
  return {
    stockPools: data?.stock_pools ?? [],
    dailyRows: data?.daily_allocation_capacities ?? [],
    campaignQuotas: data?.campaign_quotas ?? [],
  };
}

export function Inventory({ accessToken }: { accessToken: string }) {
  const [initialRange] = useState(getInitialJstDateRange);
  const [dailyFrom, setDailyFrom] = useState(initialRange.daily_from);
  const [dailyTo, setDailyTo] = useState(initialRange.daily_to);
  const [appliedRange, setAppliedRange] = useState(initialRange);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [dialogTarget, setDialogTarget] = useState<QuotaDialogTarget | null>(
    null,
  );
  const [stockDialogTarget, setStockDialogTarget] =
    useState<StockDialogTarget | null>(null);
  const [campaignId, setCampaignId] = useState("");
  const [monthInput, setMonthInput] = useState("");
  const [newCapacityInput, setNewCapacityInput] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [quotaState, dispatchQuota] = useReducer(
    campaignQuotaReducer,
    initialCampaignQuotaState,
  );

  const queryString = buildAdminInventoryQuery(appliedRange);
  const inventoryQuery = useQuery({
    queryKey: [
      "admin-inventory",
      appliedRange.daily_from,
      appliedRange.daily_to,
    ],
    queryFn: ({ signal }) =>
      fetchAdminInventory(accessToken, queryString, signal),
    refetchOnWindowFocus: true,
    retry: shouldRetry,
  });
  const rows = inventoryRows(inventoryQuery.data);
  // 409 後の refetch を Dialog にも効かせる（設計書 §5-4 の競合回復）。
  const activeQuota =
    dialogTarget?.mode === "update"
      ? resolveCampaignQuotaRow(dialogTarget.quota, rows.campaignQuotas)
      : null;

  const previewMutation = useMutation({
    mutationFn: (request: Parameters<typeof setCampaignQuotaCapacity>[1]) =>
      setCampaignQuotaCapacity(accessToken, request),
    onMutate: () => dispatchQuota({ type: "preview_started" }),
    onSuccess: (preview, request) =>
      dispatchQuota({ type: "preview_received", request, preview }),
    onError: async (error) => {
      if (
        error instanceof AdminApiError &&
        error.code === "campaign_quota_capacity_conflict"
      ) {
        dispatchQuota({ type: "conflict_received" });
        await inventoryQuery.refetch();
        return;
      }
      dispatchQuota({ type: "preview_failed" });
    },
  });

  const executeMutation = useMutation({
    mutationFn: (request: Parameters<typeof setCampaignQuotaCapacity>[1]) =>
      setCampaignQuotaCapacity(accessToken, request),
    onMutate: () => dispatchQuota({ type: "execute_started" }),
    onSuccess: async (result) => {
      dispatchQuota({ type: "execute_received", result });
      setDialogTarget(null);
      await inventoryQuery.refetch();
      toast.success(
        result.result === "noop"
          ? "すでに同じ枠です（変更なし）"
          : result.result === "created"
            ? "月次枠を作成しました。"
            : "月次枠を変更しました。",
      );
    },
    onError: async (error) => {
      if (
        error instanceof AdminApiError &&
        error.code === "campaign_quota_capacity_conflict"
      ) {
        dispatchQuota({ type: "conflict_received" });
        await inventoryQuery.refetch();
        return;
      }
      if (
        error instanceof AdminApiError &&
        error.code === "campaign_quota_capacity_below_usage"
      ) {
        dispatchQuota({ type: "reset" });
        return;
      }
      dispatchQuota({ type: "execute_failed" });
    },
  });

  const busy = previewMutation.isPending || executeMutation.isPending;

  function applyRange(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validation = validateDailyDateRange(dailyFrom, dailyTo);
    if (!validation.ok) {
      setRangeError("入力内容を確認してください。");
      return;
    }
    setRangeError(null);
    if (
      dailyFrom === appliedRange.daily_from &&
      dailyTo === appliedRange.daily_to
    ) {
      void inventoryQuery.refetch();
    } else {
      setAppliedRange({ daily_from: dailyFrom, daily_to: dailyTo });
    }
  }

  function openCreateDialog() {
    setDialogTarget({ mode: "create" });
    setCampaignId("");
    setMonthInput("");
    setNewCapacityInput("");
    setReason("");
    setFormError(null);
    previewMutation.reset();
    executeMutation.reset();
    dispatchQuota({ type: "reset" });
  }

  function openUpdateDialog(quota: AdminCampaignQuotaRow) {
    setDialogTarget({ mode: "update", quota });
    setCampaignId(quota.campaign_id);
    setMonthInput(quota.month.slice(0, 7));
    setNewCapacityInput(String(quota.capacity));
    setReason("");
    setFormError(null);
    previewMutation.reset();
    executeMutation.reset();
    dispatchQuota({ type: "reset" });
  }

  function inputChanged() {
    setFormError(null);
    previewMutation.reset();
    executeMutation.reset();
    dispatchQuota({ type: "input_changed" });
  }

  function previewChanges(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!dialogTarget || busy) return;
    const validation = buildCampaignQuotaRequest(
      activeQuota === null
        ? { mode: "create", campaignId, monthInput, newCapacityInput, reason }
        : {
            mode: "update",
            quota: activeQuota,
            newCapacityInput,
            reason,
          },
    );
    if (!validation.ok) {
      setFormError("入力内容を確認してください。");
      return;
    }
    setFormError(null);
    // 再プレビューは競合・下限エラーからの回復手段なので、前回確定の
    // エラー表示を残さない（新しい preview 結果と併記されるのを防ぐ）。
    executeMutation.reset();
    previewMutation.mutate(validation.request);
  }

  function executeChanges() {
    if (!canExecuteCampaignQuota(quotaState) || executeMutation.isPending)
      return;
    const request = createExecuteRequest(quotaState);
    if (request) executeMutation.mutate(request);
  }

  const permissionError =
    inventoryQuery.error instanceof AdminApiError &&
    (inventoryQuery.error.status === 401 ||
      inventoryQuery.error.status === 403);
  if (permissionError) {
    return (
      <Alert
        variant="destructive"
        className={`${inventoryAlertClass} mx-auto max-w-2xl`}
      >
        <AlertTitle>権限がありません</AlertTitle>
        <AlertDescription>{permissionErrorMessage}</AlertDescription>
      </Alert>
    );
  }

  const mutationError = executeMutation.error ?? previewMutation.error;
  const conflict = quotaState.phase === "idle" && quotaState.conflict === true;
  // 作成中の 409 は「他の管理者が同じ campaign/month を先に作った」以外に
  // 起きない。create のまま再プレビューしても expected は NULL のままで
  // 再び 409 になるため、変更操作へ誘導する。
  const createCollision =
    conflict && dialogTarget?.mode === "create"
      ? findCampaignQuotaRow(
          rows.campaignQuotas,
          campaignId.trim(),
          normalizeMonthInput(monthInput) ?? "",
        )
      : undefined;
  const belowUsage =
    mutationError instanceof AdminApiError &&
    mutationError.code === "campaign_quota_capacity_below_usage";

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="grid gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">在庫・枠</h1>
          <p className="text-sm text-zinc-500">
            {inventoryQuery.data ? (
              <>
                <span
                  {...dateDisplayProps(
                    inventoryQuery.data.generated_at,
                    formatDateTime,
                  )}
                />{" "}
                時点のデータ
              </>
            ) : (
              ""
            )}
          </p>
        </div>
        <Button
          className={inventorySecondaryButtonClass}
          variant="outline"
          onClick={() => void inventoryQuery.refetch()}
          disabled={inventoryQuery.isFetching}
        >
          {inventoryQuery.isFetching ? "更新中…" : "今すぐ更新"}
        </Button>
      </div>

      <Card className={inventoryCardClass}>
        <CardContent className="pt-6">
          <form
            className="flex flex-wrap items-end gap-4"
            onSubmit={applyRange}
          >
            <div className="grid gap-2">
              <Label htmlFor="inventory-daily-from">開始日</Label>
              <Input
                className={inventoryControlClass}
                id="inventory-daily-from"
                type="date"
                value={dailyFrom}
                onChange={(event) => setDailyFrom(event.target.value)}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inventory-daily-to">終了日</Label>
              <Input
                className={inventoryControlClass}
                id="inventory-daily-to"
                type="date"
                value={dailyTo}
                onChange={(event) => setDailyTo(event.target.value)}
                required
              />
            </div>
            <Button className={inventoryPrimaryButtonClass} type="submit">
              表示を更新
            </Button>
          </form>
          {rangeError ? (
            <Alert variant="destructive" className={inventoryAlertClass}>
              <AlertDescription>{rangeError}</AlertDescription>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      {inventoryQuery.isError ? (
        <Alert variant="destructive" className={inventoryAlertClass}>
          <AlertTitle>在庫・枠を取得できませんでした</AlertTitle>
          <AlertDescription>
            {loadErrorMessage(inventoryQuery.error)}
          </AlertDescription>
        </Alert>
      ) : null}
      {inventoryQuery.isPending ? (
        <div className="grid min-h-48 place-items-center border border-zinc-300 bg-white text-sm text-zinc-500">
          在庫・枠を読み込んでいます…
        </div>
      ) : null}

      {inventoryQuery.data ? (
        <>
          <Card className={inventoryCardClass}>
            <CardHeader>
              <CardTitle id="stock-pools-heading">
                品種別年産在庫（玄米換算 g・読み取りのみ）
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StockPoolsTable
                rows={rows.stockPools}
                onOpen={(kind, pool) => setStockDialogTarget({ kind, pool })}
              />
            </CardContent>
          </Card>
          <Card className={inventoryCardClass}>
            <CardHeader>
              <CardTitle id="daily-capacities-heading">
                日次出荷枠（件・読み取りのみ）
              </CardTitle>
              <CardDescription>
                既定値の行はまだ DB
                に作成されておらず、割当時に保存されます。既定値は{" "}
                {formatCapacityCount(
                  inventoryQuery.data.daily_capacity_default,
                )}{" "}
                です。
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DailyCapacitiesTable rows={rows.dailyRows} />
            </CardContent>
          </Card>
          <Card className={inventoryCardClass}>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle id="campaign-quotas-heading">
                  キャンペーン月次枠（件・作成/変更）
                </CardTitle>
              </div>
              <Button
                className={inventoryPrimaryButtonClass}
                onClick={openCreateDialog}
              >
                月次枠を作成
              </Button>
            </CardHeader>
            <CardContent>
              <Table aria-labelledby="campaign-quotas-heading">
                <TableHeader>
                  <TableRow>
                    <TableHead>キャンペーン</TableHead>
                    <TableHead>対象月</TableHead>
                    <TableHead>総枠</TableHead>
                    <TableHead>仮確保</TableHead>
                    <TableHead>消費</TableHead>
                    <TableHead>残り</TableHead>
                    <TableHead>更新日時</TableHead>
                    <TableHead>操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.campaignQuotas.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="py-8 text-center text-zinc-500"
                      >
                        月次枠はありません。販売前に月次枠を作成してください。
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.campaignQuotas.map((quota) => (
                      <TableRow key={quota.id}>
                        <TableCell>{quota.campaign_id}</TableCell>
                        <TableCell
                          {...dateDisplayProps(quota.month, formatMonth)}
                        />
                        <TableCell>
                          {formatCapacityCount(quota.capacity)}
                        </TableCell>
                        <TableCell>{formatCapacityCount(quota.held)}</TableCell>
                        <TableCell>
                          {formatCapacityCount(quota.consumed)}
                        </TableCell>
                        <TableCell>
                          {formatCapacityCount(quota.available)}
                        </TableCell>
                        <TableCell
                          {...dateDisplayProps(
                            quota.updated_at,
                            formatDateTime,
                          )}
                        />
                        <TableCell>
                          <Button
                            className={inventorySecondaryButtonClass}
                            variant="outline"
                            size="sm"
                            onClick={() => openUpdateDialog(quota)}
                          >
                            変更
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      ) : null}

      <Dialog
        open={dialogTarget !== null}
        onOpenChange={(open) => {
          if (!open && !busy) setDialogTarget(null);
        }}
      >
        <DialogContent
          className="max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none"
          aria-labelledby="campaign-quota-dialog-title"
          aria-describedby="campaign-quota-dialog-description"
          showCloseButton={!busy}
        >
          <DialogHeader>
            <DialogTitle id="campaign-quota-dialog-title">
              {dialogTarget?.mode === "create"
                ? "月次枠を作成"
                : "月次枠を変更"}
            </DialogTitle>
            <DialogDescription id="campaign-quota-dialog-description">
              変更内容をプレビューしてから確定します。
            </DialogDescription>
          </DialogHeader>
          <form className="grid gap-4 px-6" onSubmit={previewChanges}>
            <div className="grid gap-2">
              <Label htmlFor="campaign-quota-id">キャンペーン ID</Label>
              <Input
                className={inventoryControlClass}
                id="campaign-quota-id"
                value={campaignId}
                disabled={dialogTarget?.mode === "update" || busy}
                onChange={(event) => {
                  setCampaignId(event.target.value);
                  inputChanged();
                }}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="campaign-quota-month">対象月（月単位）</Label>
              <Input
                className={inventoryControlClass}
                id="campaign-quota-month"
                type="month"
                value={monthInput}
                disabled={dialogTarget?.mode === "update" || busy}
                onChange={(event) => {
                  setMonthInput(event.target.value);
                  inputChanged();
                }}
                required
              />
            </div>
            {activeQuota ? (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <p>
                  現在の総枠
                  <br />
                  <strong>{formatCapacityCount(activeQuota.capacity)}</strong>
                </p>
                <p>
                  仮確保
                  <br />
                  <strong>{formatCapacityCount(activeQuota.held)}</strong>
                </p>
                <p>
                  消費
                  <br />
                  <strong>{formatCapacityCount(activeQuota.consumed)}</strong>
                </p>
                <p>
                  変更可能な最小値
                  <br />
                  <strong>
                    {formatCapacityCount(
                      activeQuota.held + activeQuota.consumed,
                    )}
                  </strong>
                </p>
              </div>
            ) : null}
            <div className="grid gap-2">
              <Label htmlFor="campaign-quota-capacity">新しい総枠</Label>
              <Input
                className={inventoryControlClass}
                id="campaign-quota-capacity"
                type="number"
                min={0}
                step={1}
                inputMode="numeric"
                value={newCapacityInput}
                disabled={busy}
                onChange={(event) => {
                  setNewCapacityInput(event.target.value);
                  inputChanged();
                }}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="campaign-quota-reason">理由</Label>
              <Textarea
                className={inventoryControlClass}
                id="campaign-quota-reason"
                value={reason}
                maxLength={500}
                disabled={busy}
                onChange={(event) => {
                  setReason(event.target.value);
                  inputChanged();
                }}
                required
              />
            </div>
            <Button
              className={inventoryPrimaryButtonClass}
              type="submit"
              disabled={busy}
            >
              {previewMutation.isPending ? "確認中…" : "変更内容をプレビュー"}
            </Button>
          </form>
          <div className="grid gap-4 px-6">
            {formError ? (
              <Alert variant="destructive" className={inventoryAlertClass}>
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            ) : null}
            {conflict ? (
              <Alert variant="destructive" className={inventoryAlertClass}>
                <AlertDescription>
                  {createCollision
                    ? "この対象月の月次枠はすでに作成されています。閉じて、一覧の「変更」から操作してください。"
                    : "プレビュー後に月次枠が変更されました。最新値を取得して、もう一度プレビューしてください。"}
                </AlertDescription>
              </Alert>
            ) : null}
            {belowUsage ? (
              <Alert variant="destructive" className={inventoryAlertClass}>
                <AlertDescription>
                  現在の仮確保と消費の合計を下回る値には変更できません。再入力して、もう一度プレビューしてください。
                </AlertDescription>
              </Alert>
            ) : null}
            {mutationError && !conflict && !belowUsage ? (
              <Alert variant="destructive" className={inventoryAlertClass}>
                <AlertDescription>
                  {loadErrorMessage(mutationError)}
                </AlertDescription>
              </Alert>
            ) : null}
            {quotaState.phase === "ready" ||
            quotaState.phase === "executing" ? (
              <div className="grid gap-3 border border-zinc-300 p-4 text-sm">
                <p>
                  操作:{" "}
                  {quotaState.preview.operation === "create"
                    ? "作成"
                    : quotaState.preview.operation === "update"
                      ? "変更"
                      : "変更なし"}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <p>
                    現在値
                    <br />
                    <strong>
                      {quotaState.preview.previous_capacity === null
                        ? "—"
                        : formatCapacityCount(
                            quotaState.preview.previous_capacity,
                          )}
                    </strong>
                  </p>
                  <p>
                    変更後
                    <br />
                    <strong>
                      {formatCapacityCount(quotaState.preview.new_capacity)}
                    </strong>
                  </p>
                  <p>
                    仮確保
                    <br />
                    <strong>
                      {formatCapacityCount(quotaState.preview.held)}
                    </strong>
                  </p>
                  <p>
                    消費
                    <br />
                    <strong>
                      {formatCapacityCount(quotaState.preview.consumed)}
                    </strong>
                  </p>
                  <p>
                    変更後残数
                    <br />
                    <strong>
                      {formatCapacityCount(quotaState.preview.available_after)}
                    </strong>
                  </p>
                  <p>
                    理由
                    <br />
                    <strong>{quotaState.request.reason}</strong>
                  </p>
                </div>
                {quotaState.preview.operation === "noop" ? (
                  <Alert className={inventoryAlertClass}>
                    <AlertDescription>変更はありません</AlertDescription>
                  </Alert>
                ) : (
                  <label className="flex items-center gap-3 font-medium">
                    <input
                      type="checkbox"
                      className="size-4 accent-amber-500 outline-amber-500"
                      checked={
                        quotaState.phase === "ready"
                          ? quotaState.confirmed
                          : true
                      }
                      disabled={quotaState.phase !== "ready"}
                      onChange={(event) =>
                        dispatchQuota({
                          type: "confirm_toggled",
                          confirmed: event.target.checked,
                        })
                      }
                    />
                    表示した内容を確認しました
                  </label>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              className={inventorySecondaryButtonClass}
              variant="outline"
              disabled={busy}
              onClick={() => setDialogTarget(null)}
            >
              閉じる
            </Button>
            {(quotaState.phase === "ready" ||
              quotaState.phase === "executing") &&
            quotaState.preview.operation !== "noop" ? (
              <Button
                className={inventoryPrimaryButtonClass}
                onClick={executeChanges}
                disabled={
                  !canExecuteCampaignQuota(quotaState) ||
                  executeMutation.isPending
                }
              >
                {executeMutation.isPending ? "確定中…" : "月次枠を確定"}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {stockDialogTarget ? (
        <StockPoolDialog
          key={`${stockDialogTarget.kind}:${stockDialogTarget.pool.id}`}
          accessToken={accessToken}
          kind={stockDialogTarget.kind}
          target={stockDialogTarget.pool}
          rows={rows.stockPools}
          onClose={() => setStockDialogTarget(null)}
          onCompleted={async () => {
            await inventoryQuery.refetch();
          }}
          onSwitchToShortage={() =>
            setStockDialogTarget({
              kind: "shortage",
              pool: stockDialogTarget.pool,
            })
          }
        />
      ) : null}
    </div>
  );
}
